/**
 * 🏆 AffiliateKonstantleiterPage - Level & Gamification System
 */

import React from 'react';
import { AffiliateLayout } from '@/components/affiliate/layout/AffiliateLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { 
  Trophy, 
  Star, 
  Target, 
  Gift,
  CheckCircle2,
  Lock
} from 'lucide-react';

// ============================================
// TYPES & MOCK DATA
// ============================================

interface Level {
  level: number;
  name: string;
  minPoints: number;
  maxPoints: number;
  bonus: number;
  perks: string[];
  unlocked: boolean;
}

const LEVELS: Level[] = [
  { level: 1, name: 'Starter', minPoints: 0, maxPoints: 100, bonus: 0, perks: ['Basis-Provisionen', 'Referral-Link'], unlocked: true },
  { level: 2, name: 'Bronze', minPoints: 100, maxPoints: 250, bonus: 500, perks: ['Erhöhte Provisionen (+5%)', 'Marketing-Material'], unlocked: true },
  { level: 3, name: 'Silber', minPoints: 250, maxPoints: 500, bonus: 1000, perks: ['Erhöhte Provisionen (+10%)', 'Priorisierter Support'], unlocked: true },
  { level: 4, name: 'Gold', minPoints: 500, maxPoints: 1000, bonus: 2500, perks: ['Erhöhte Provisionen (+15%)', 'VIP Events', 'Persönlicher Ansprechpartner'], unlocked: false },
  { level: 5, name: 'Platin', minPoints: 1000, maxPoints: 2500, bonus: 5000, perks: ['Erhöhte Provisionen (+20%)', 'Exklusive Deals', 'Quartals-Bonus'], unlocked: false },
  { level: 6, name: 'Diamant', minPoints: 2500, maxPoints: 99999, bonus: 10000, perks: ['Maximum Provisionen (+25%)', 'Top-Partner Status', 'Individuelle Konditionen'], unlocked: false },
];

const USER_PROGRESS = {
  currentLevel: 3,
  currentPoints: 366,
  nextLevelPoints: 500,
  totalEarned: 5340.20,
  conversions: 18,
  bonusesCollected: 1500,
};

// ============================================
// COMPONENTS
// ============================================

const LevelCard: React.FC<{ level: Level; isCurrent: boolean }> = ({ level, isCurrent }) => {
  const getIcon = () => {
    if (level.level <= 2) return <Star className="w-6 h-6" />;
    if (level.level <= 4) return <Trophy className="w-6 h-6" />;
    return <Target className="w-6 h-6" />;
  };

  const getColor = () => {
    if (!level.unlocked) return 'bg-gray-100 text-gray-400';
    switch (level.level) {
      case 1: return 'bg-gray-100 text-gray-600';
      case 2: return 'bg-amber-100 text-amber-700';
      case 3: return 'bg-gray-200 text-gray-700';
      case 4: return 'bg-yellow-100 text-yellow-700';
      case 5: return 'bg-blue-100 text-blue-700';
      case 6: return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <Card className={`relative transition-colors ${
      isCurrent ? 'ring-2 ring-primary shadow-lg' : ''
    } ${!level.unlocked ? 'opacity-60' : ''}`}>
      {isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-3 py-1 rounded-full text-xs font-medium">
          Aktuell
        </div>
      )}
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-3 rounded-xl ${getColor()}`}>
            {getIcon()}
          </div>
          <div>
            <h3 className="font-bold text-lg">Level {level.level}</h3>
            <p className="text-sm text-gray-500">{level.name}</p>
          </div>
          {!level.unlocked && (
            <Lock className="w-5 h-5 text-gray-400 ml-auto" />
          )}
        </div>

        <div className="space-y-2 mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Vorteile</p>
          {level.perks.map((perk, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <CheckCircle2 className={`w-4 h-4 ${level.unlocked ? 'text-primary' : 'text-gray-300'}`} />
              <span className={level.unlocked ? 'text-gray-700' : 'text-gray-400'}>{perk}</span>
            </div>
          ))}
        </div>

        {level.bonus > 0 && (
          <div className={`p-3 rounded-lg ${level.unlocked ? 'bg-green-50' : 'bg-gray-50'}`}>
            <div className="flex items-center gap-2">
              <Gift className={`w-4 h-4 ${level.unlocked ? 'text-green-600' : 'text-gray-400'}`} />
              <span className={`text-sm font-medium ${level.unlocked ? 'text-green-700' : 'text-gray-400'}`}>
                Level-Bonus: €{level.bonus.toLocaleString('de-DE')}
              </span>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-4 text-center">
          {level.minPoints} - {level.maxPoints < 99999 ? level.maxPoints : '∞'} Punkte
        </p>
      </CardContent>
    </Card>
  );
};

// ============================================
// MAIN PAGE
// ============================================

export const AffiliateKonstantleiterPage: React.FC = () => {
  const currentLevel = LEVELS.find(l => l.level === USER_PROGRESS.currentLevel)!;
  const progressPercent = ((USER_PROGRESS.currentPoints - currentLevel.minPoints) / 
    (USER_PROGRESS.nextLevelPoints - currentLevel.minPoints)) * 100;

  return (
    <AffiliateLayout
      pageTitle="Konstantleiter"
      pageSubtitle="Steige Level auf und erhalte exklusive Vorteile"
    >
      {/* Progress Overview */}
      <Card className="mb-6 bg-gradient-to-r from-primary/5 to-primary/10">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-primary/20 rounded-2xl">
                <Trophy className="w-10 h-10 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Level {USER_PROGRESS.currentLevel} - {currentLevel.name}</h2>
                <p className="text-gray-600">Du bist auf dem Weg zu Level {USER_PROGRESS.currentLevel + 1}!</p>
              </div>
            </div>
            
            <div className="flex-1 md:max-w-md">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>{USER_PROGRESS.currentPoints} Punkte</span>
                <span>{USER_PROGRESS.nextLevelPoints} Punkte</span>
              </div>
              <Progress value={progressPercent} className="h-3" />
              <p className="text-sm text-gray-500 mt-2">
                Noch {USER_PROGRESS.nextLevelPoints - USER_PROGRESS.currentPoints} Punkte bis Level {USER_PROGRESS.currentLevel + 1}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <p className="text-sm text-gray-500">Gesammelte Punkte</p>
          <p className="text-2xl font-bold">{USER_PROGRESS.currentPoints}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">Conversions</p>
          <p className="text-2xl font-bold">{USER_PROGRESS.conversions}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">Bonusse erhalten</p>
          <p className="text-2xl font-bold text-primary">€{USER_PROGRESS.bonusesCollected.toLocaleString('de-DE')}</p>
        </Card>
      </div>

      {/* How to earn points */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base font-semibold">So sammelst du Punkte</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary font-bold">
                +10
              </div>
              <div>
                <p className="font-medium text-sm">Lead erfasst</p>
                <p className="text-xs text-gray-500">Pro neuem Lead</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary font-bold">
                +50
              </div>
              <div>
                <p className="font-medium text-sm">Conversion</p>
                <p className="text-xs text-gray-500">Pro konvertiertem Restaurant</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary font-bold">
                +5
              </div>
              <div>
                <p className="font-medium text-sm">Aktives Lokal</p>
                <p className="text-xs text-gray-500">Pro Monat pro aktivem Restaurant</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 font-bold">
                +?
              </div>
              <div>
                <p className="font-medium text-sm">Bonus-Aktionen</p>
                <p className="text-xs text-gray-500">Saisonale Specials</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Level Grid */}
      <h3 className="text-lg font-semibold mb-4">Alle Level</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {LEVELS.map(level => (
          <LevelCard 
            key={level.level} 
            level={level} 
            isCurrent={level.level === USER_PROGRESS.currentLevel}
          />
        ))}
      </div>
    </AffiliateLayout>
  );
};

export default AffiliateKonstantleiterPage;
